import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaMapMarkerAlt } from 'react-icons/fa';
import { FiCalendar, FiMapPin, FiPhone, FiUser, FiSearch, FiZoomIn, FiX } from 'react-icons/fi';
import { getPosterById, getRepresentations } from '../api/publicApi';
import useFetch from '../hooks/useFetch';
import { SkeletonBox } from '../components/Skeleton';
import { useState, useRef, useEffect } from 'react';
import worldwidelogo from '../assets/allCountries.png';

const STATUT_LABELS = { prevu: 'À VENIR', en_cours: 'EN COURS', termine: 'TERMINÉ' };

export default function PosterDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const debounceRef = useRef(null);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(val), 300);
  };

  /* ── Fetch poster by id ── */
  const { data: posterData, loading: posterLoading, error: posterError } = useFetch(
    getPosterById, id, [id]
  );

  /* ── Fetch all representations to find the matching country ── */
  const { data: repsData } = useFetch(getRepresentations, { limit: 100, statut: 'active', }, []);

  const poster = posterData?.data || posterData || null;
  const allReps = repsData?.data || [];
  const filteredReps = allReps.filter(r => !debouncedSearch || (r.pays || '').toLowerCase().includes(debouncedSearch.toLowerCase()));

  /* Auto-match representation by poster.pays_concernes */
  const rep = poster?.pays_concernes
    ? allReps.find(r => r.pays?.toLowerCase() === poster.pays_concernes?.toLowerCase())
    : null;

  const handleCountrySelect = (country) => {
    navigate('/evenements', { state: { selectedCountryId: country.id } });
  };

  // Close lightbox on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <div className="two-col">

      <div className="rep-mobile-top">
        <div className="search-bar">
          <FiSearch size={18} />
          <input placeholder="RECHERCHER" value={search} onChange={e => handleSearch(e.target.value)} />
        </div>
        <div className="country-row hide-scrollbar">
          <button className="country-btn" onClick={() => navigate('/evenements')}>
            <FaMapMarkerAlt size={18} />
            <span>Tous les pays</span>
          </button>
          {(filteredReps || []).map(r => (
            <button
              key={r.id}
              className={`country-btn${rep?.id === r.id ? ' selected' : ''}${r.is_headquarter ? ' headquarters' : ''}`}
              onClick={() => handleCountrySelect(r)}
              aria-label={`${r.pays}${r.is_headquarter ? ', siège' : ''}`}
            >
              {r.drapeau_url ? (
                <img src={r.drapeau_url} alt={r.pays} style={{ width: 32, height: 'auto', borderRadius: 2 }} />
              ) : (
                <FaMapMarkerAlt size={18} />
              )}
              <span>{r.pays}</span>
              {r.is_headquarter && <span className="headquarters-badge">SIÈGE</span>}
            </button>
          ))}
        </div>
      </div>

      {/* ── LEFT: country selector sidebar, same as events list page ── */}
      <aside className="sidebar-col">
        <div className="search-bar">
          <FiSearch size={18} />
          <input placeholder="RECHERCHER" value={search} onChange={e => handleSearch(e.target.value)} />
        </div>
        <div className="country-grid" style={{ paddingTop: 12 }}>
          <button
            className="country-btn"
            onClick={() => navigate('/evenements')}
          >
            <FaMapMarkerAlt size={18} />
            <span>Tous les pays</span>
          </button>
          {(filteredReps || []).map(r => (
            <button
              key={r.id}
              className={`country-btn${rep?.id === r.id ? ' selected' : ''}${r.is_headquarter ? ' headquarters' : ''}`}
              onClick={() => handleCountrySelect(r)}
              aria-label={`${r.pays}${r.is_headquarter ? ', siège' : ''}`}
            >
              {r.drapeau_url ? (
                <img src={r.drapeau_url} alt={r.pays} style={{ width: 32, height: 'auto', borderRadius: 2 }} />
              ) : (
                <FaMapMarkerAlt size={18} />
              )}
              <span>{r.pays}</span>
              {r.is_headquarter && <span className="headquarters-badge">SIÈGE</span>}
            </button>
          ))}
        </div>
      </aside>

      {/* ── RIGHT: poster detail ── */}
      <div className="main-col" style={{ paddingTop: 24 }}>

        {/* Back link */}
        <button className="contact-back-link" style={{ marginBottom: 16 }} onClick={() => navigate(-1)}>
          <FaArrowLeft size={12} /> Retour aux événements
        </button>

        {posterLoading ? (
          <div>
            <SkeletonBox height={28} width="50%" style={{ marginBottom: 20 }} />
            <SkeletonBox height={14} style={{ marginBottom: 10 }} />
            <SkeletonBox height={14} width="80%" style={{ marginBottom: 10 }} />
            <SkeletonBox height={14} width="60%" style={{ marginBottom: 24 }} />
            <SkeletonBox height={200} borderRadius={6} />
          </div>
        ) : posterError ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
            <FiCalendar size={36} style={{ display: 'block', margin: '0 auto 12px' }} />
            <p>Impossible de charger cet événement.</p>
          </div>
        ) : !poster ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
            <FiCalendar size={36} style={{ display: 'block', margin: '0 auto 12px' }} />
            <p>Événement introuvable.</p>
          </div>
        ) : (
          <>
            {/* Country heading with flag */}
            <h3 className='bg-white p-3 text-capitalize' style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'serif', fontWeight: 400, fontSize: '1.4rem', marginBottom: 16 }}>
              {rep?.drapeau_url ? (
                <img src={rep.drapeau_url} alt={poster.pays_concernes} style={{ width: 32, height: 'auto', borderRadius: 2 }} />
              ) : (
                <img src={worldwidelogo} alt={'worldwide'} style={{ width: 32, height: 'auto', borderRadius: 2 }} />
              )}
              {poster.pays_concernes || poster.titre_evenement}
            </h3>

            {/* Description */}
            {poster.description && (
              <p style={{ fontSize: 13, color: '#555', lineHeight: 1.7, marginBottom: 20 }}>
                <strong>Description:</strong><br />{poster.description}
              </p>
            )}

            {/* venue-detail: info box + poster image side by side */}
            <div className="row">
              {/* <div className="p-3 border border-muted col-md-8 col-12 bg-white" style={{ fontSize: '13px' }}>
                {poster.titre_evenement && (
                  <div className="venue-info-row">
                    <div className="lbl">Titre de l'événement:</div>
                    <div className="val text-capitalize">{poster.titre_evenement}</div>
                  </div>
                )}
                {poster.date_evenement && (
                  <div className="venue-info-row">
                    <div className="lbl">Date de l'événement:</div>
                    <div className="val">
                      {new Date(poster.date_evenement)
                        .toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                  </div>
                )}
                {poster.day_of_week && (
                  <div className="venue-info-row">
                    <div className="lbl">Jour de rencontre:</div>
                    <div className="val text-capitalize">{poster.day_of_week}</div>
                  </div>
                )}
                {poster.time_event && (
                  <div className="venue-info-row">
                    <div className="lbl">Heure de l'événement:</div>
                    <div className="val">{poster.time_event}</div>
                  </div>
                )}
              </div> */}
              {poster.image_url && (
                <div className='col-d-4 col-12 d-flex align-items-center justify-content-center'>
                  <div className="poster-container d-flex align-items-center justify-content-center">
                    <img
                      src={poster.image_url}
                      alt={poster.titre_evenement}
                      className="venue-poster w-50"
                      onClick={() => setLightboxOpen(true)}
                    />
                  </div>
                </div>
              )}

              {/* Lightbox Modal */}
              {lightboxOpen && (
                <div
                  className="lightbox-overlay"
                  onClick={() => setLightboxOpen(false)}
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.85)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    animation: 'fadeIn 0.2s ease-in',
                  }}
                >
                  <div
                    className="lightbox-content"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'relative',
                      maxWidth: '90vw',
                      maxHeight: '90vh',
                      animation: 'zoomIn 0.3s ease-out',
                    }}
                  >
                    <img
                      src={poster.image_url}
                      alt={poster.titre_evenement}
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '90vh',
                        objectFit: 'contain',
                        borderRadius: '4px',
                      }}
                    />
                    <button
                      className="lightbox-close"
                      onClick={() => setLightboxOpen(false)}
                      title="Fermer"
                      style={{
                        position: 'absolute',
                        top: -40,
                        right: 0,
                        background: 'none',
                        border: 'none',
                        color: 'white',
                        fontSize: '28px',
                        cursor: 'pointer',
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'transform 0.2s',
                      }}
                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.2)'}
                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                    >
                      <FiX size={28} />
                    </button>
                    <p style={{ color: '#999', textAlign: 'center', marginTop: 16, fontSize: 12 }}>
                      Cliquez pour fermer ou appuyez sur Échap
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
